import React from "react";
import { useTranslation } from "react-i18next";
import { Bell } from "lucide-react";
import { notificationsApi, type InboxNotification } from "../lib/api";
import { useNotificationStream } from "../hooks/useNotificationStream";

interface NotificationBellProps {
  onNavigate?: () => void; // appelé quand l'user clique "Tout voir"
}

const POPOVER_LIMIT = 10;

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.round(h / 24);
  return `il y a ${d} j`;
}

export function NotificationBell({ onNavigate }: NotificationBellProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState<InboxNotification[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  const refresh = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await notificationsApi.getInbox({ limit: POPOVER_LIMIT });
      setItems(data.items);
      setUnreadCount(data.unreadCount);
    } catch {
      // Silencieux : un user non connecté ou erreur transitoire ne doit pas casser l'UI
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // Live updates via SSE
  useNotificationStream(true, (incoming) => {
    setItems((prev) => [
      { ...incoming, readAt: null },
      ...prev.filter((it) => it.id !== incoming.id),
    ].slice(0, POPOVER_LIMIT));
    setUnreadCount((c) => c + 1);
  });

  async function handleClickItem(it: InboxNotification) {
    if (!it.readAt) {
      try {
        await notificationsApi.markRead(it.id);
        setItems((prev) => prev.map((p) => (p.id === it.id ? { ...p, readAt: new Date().toISOString() } : p)));
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        // ignore
      }
    }
    if (it.ctaUrl) {
      setOpen(false);
      window.location.href = it.ctaUrl;
    }
  }

  const badge = unreadCount > 9 ? "9+" : unreadCount > 0 ? String(unreadCount) : null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative flex items-center justify-center h-8 w-8 rounded-full hover:bg-muted transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4 text-muted-foreground" />
        {badge && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
            {badge}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Overlay pour fermer au clic extérieur */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-20 w-80 bg-white rounded-lg shadow-lg border overflow-hidden">
            <div className="px-4 py-2 border-b flex items-center justify-between bg-muted/30">
              <span className="text-sm font-semibold">Notifications</span>
              {unreadCount > 0 && (
                <button
                  className="text-xs text-blue-600 hover:underline"
                  onClick={async () => {
                    await notificationsApi.markAllRead().catch(() => {});
                    setItems((prev) => prev.map((p) => ({ ...p, readAt: p.readAt ?? new Date().toISOString() })));
                    setUnreadCount(0);
                  }}
                >
                  Tout marquer lu
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {loading && items.length === 0 && (
                <div className="px-4 py-6 text-center text-xs text-muted-foreground">Chargement…</div>
              )}
              {!loading && items.length === 0 && (
                <div className="px-4 py-6 text-center text-xs text-muted-foreground">Aucune notification</div>
              )}
              {items.map((it) => (
                <button
                  key={it.id}
                  onClick={() => handleClickItem(it)}
                  className={`w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-muted/50 transition-colors ${
                    !it.readAt ? "bg-blue-50/40" : ""
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!it.readAt && <span className="mt-1.5 inline-block w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{it.title}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2">{it.body}</div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground">{timeAgo(it.createdAt)}</span>
                        {/* Rend l'action visible : la carte était cliquable sans
                            que rien ne l'indique. */}
                        {it.ctaUrl && (
                          <span className="text-[10px] font-semibold" style={{ color: "#5A4BBE" }}>
                            {it.ctaLabel || t("notifications.open")} →
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {onNavigate && (
              <button
                onClick={() => {
                  setOpen(false);
                  onNavigate();
                }}
                className="w-full text-center text-xs py-2 border-t hover:bg-muted/50 text-blue-600"
              >
                Tout voir
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
